// serviceWorkerRegistration.js

const isLocalhost = Boolean(
    window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(
        /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

let waitingRegistration = null;
const waitingListeners = new Set();

function notifyWaitingListeners(registration) {
    waitingRegistration = registration && registration.waiting ? registration : null;
    waitingListeners.forEach((listener) => listener(waitingRegistration));
}

function markRegistrationWaiting(registration) {
    if (registration && registration.waiting) {
        notifyWaitingListeners(registration);
    }
}

export function register(config) {
    if ('serviceWorker' in navigator) {
        const publicUrl = new URL(process.env.PUBLIC_URL || '/', window.location.origin)
        if (publicUrl.origin !== window.location.origin) {
            console.warn('Service worker registration skipped due to cross-origin mismatch.');
            return;
        }

        window.addEventListener('load', () => {
            const swUrl = `${publicUrl.href}service-worker.js`;

            if (isLocalhost) {
                // This is running on localhost. Let's check if a service worker still exists or not.
                checkValidServiceWorker(swUrl, config);
                navigator.serviceWorker.ready.then(() => {
                    console.log('This web app is being served cache-first by a service worker.');
                });
            } else {
                // Is not localhost. Just register service worker
                registerValidSW(swUrl, config);
            }
        });
    } else {
        console.warn('Service workers are not supported in this browser.');
    }
}

function registerValidSW(swUrl, config) {
    navigator.serviceWorker
        .register(swUrl)
        .then((registration) => {

            markRegistrationWaiting(registration);

            registration.update();
            console.log('Service worker registered successfully:', registration);

            // Auto-check for SW updates every 60 minutes.
            // Ensures installed PWAs pick up manifest/customization changes
            // without requiring a full manual page reload.
            const updateInterval = setInterval(() => {
                registration.update();
            }, 60 * 60 * 1000);

            window.addEventListener('beforeunload', () => clearInterval(updateInterval));

            registration.onupdatefound = () => {
                const installingWorker = registration.installing;
                if (installingWorker == null) {
                    return;
                }
                installingWorker.onstatechange = () => {
                    if (installingWorker.state === 'installed') {
                        if (navigator.serviceWorker.controller) {
                            // New content is available; please refresh.
                            console.log('New content is available and will be used when all tabs for this page are closed.');
                            markRegistrationWaiting(registration);
                            if (config && config.onUpdate) {
                                config.onUpdate(registration);
                            }
                        } else {
                            // Content is cached for offline use.
                            console.log('Content is cached for offline use.');
                            if (config && config.onSuccess) {
                                config.onSuccess(registration);
                            }
                        }
                    }
                };
            };
        })
        .catch((error) => {
            console.error('Error during service worker registration:', error);
        });
}

function checkValidServiceWorker(swUrl, config) {
    fetch(swUrl, {
        headers: { 'Service-Worker': 'script' },
    })
        .then((response) => {
            // Ensure service worker exists, and that we really are getting a JS file.
            const contentType = response.headers.get('content-type');
            if (
                response.status === 404 ||
                (contentType != null && contentType.indexOf('javascript') === -1)
            ) {
                // No service worker found. Probably a different app. Reload the page.
                navigator.serviceWorker.ready.then((registration) => {
                    registration.unregister().then(() => {
                        window.location.reload();
                    });
                });
            } else {
                // Service worker found. Proceed as normal.
                registerValidSW(swUrl, config);
            }
        })
        .catch(() => {
            console.log('No internet connection found. App is running in offline mode.');
        });
}

export function unregister() {
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready
            .then((registration) => {
                registration.unregister();
            })
            .catch((error) => {
                console.error(error.message);
            });
    }
}

export function subscribeToWaitingServiceWorker(listener) {
    waitingListeners.add(listener);
    listener(waitingRegistration);

    return () => {
        waitingListeners.delete(listener);
    };
}

export async function syncWaitingServiceWorker() {
    if (!('serviceWorker' in navigator)) {
        notifyWaitingListeners(null);
        return null;
    }

    const registration = await navigator.serviceWorker.getRegistration();
    markRegistrationWaiting(registration);

    if (!registration || !registration.waiting) {
        notifyWaitingListeners(null);
        return null;
    }

    return registration;
}

function waitForUpdatedWorker(registration, timeoutMs = 8000) {
    return new Promise((resolve) => {
        let settled = false;
        let watchedWorker = null;
        let removeStateChangeListener = null;
        let intervalId = null;
        let timeoutId = null;

        const finish = (result) => {
            if (settled) {
                return;
            }

            settled = true;
            if (removeStateChangeListener) {
                removeStateChangeListener();
            }
            if (intervalId) {
                window.clearInterval(intervalId);
            }
            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }
            resolve(result);
        };

        const watchInstallingWorker = (worker) => {
            if (!worker || worker === watchedWorker) {
                return;
            }

            if (removeStateChangeListener) {
                removeStateChangeListener();
            }

            watchedWorker = worker;

            if (worker.state === 'installed') {
                finish(true);
                return;
            }

            const handleStateChange = () => {
                if (worker.state === 'installed') {
                    finish(true);
                }

                if (worker.state === 'redundant') {
                    finish(false);
                }
            };

            worker.addEventListener('statechange', handleStateChange);
            removeStateChangeListener = () => {
                worker.removeEventListener('statechange', handleStateChange);
            };
        };

        if (registration.waiting) {
            finish(true);
            return;
        }

        registration.addEventListener('updatefound', () => {
            watchInstallingWorker(registration.installing);
        }, { once: true });

        watchInstallingWorker(registration.installing);

        intervalId = window.setInterval(() => {
            if (registration.waiting) {
                finish(true);
                return;
            }

            if (registration.installing && registration.installing !== watchedWorker) {
                watchInstallingWorker(registration.installing);
            }
        }, 250);

        timeoutId = window.setTimeout(() => {
            finish(false);
        }, timeoutMs);
    });
}

export async function updateApp() {
    if (!('serviceWorker' in navigator)) {
        return { status: 'unsupported' };
    }

    const registration = await navigator.serviceWorker.getRegistration();

    if (!registration) {
        return { status: 'no-registration' };
    }

    await registration.update();

    if (registration.waiting) {
        notifyWaitingListeners(registration);
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        return { status: 'activating' };
    }

    const hasNewWorker = await waitForUpdatedWorker(registration);

    if (hasNewWorker && registration.waiting) {
        notifyWaitingListeners(registration);
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        return { status: 'activating' };
    }

    return { status: 'up-to-date' };
}
